import React from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useClarificationNavigation } from '@/features/submission_forms/ClarificationNavigationContext';
import { hiddenOpenText } from '@/features/submission_forms/clarificationTargets';

interface ClarificationDialogStepperProps {
  questionSlug: string;
}

/**
 * Previous/Next footer for a field's clarification dialog: moves straight to
 * the neighbouring field with open threads and opens its dialog. Renders
 * nothing outside the submission form's ClarificationNavigationProvider.
 */
const ClarificationDialogStepper: React.FC<ClarificationDialogStepperProps> = ({
  questionSlug,
}) => {
  const { enabled, targets, hiddenUnresolvedCount, cursorFor, step } =
    useClarificationNavigation();
  const { totalClarificationCount } = useClarificationsContext();

  if (!enabled) return null;

  if (targets.length === 0) {
    if (totalClarificationCount === 0) return null;
    // Threads on branch-hidden questions are still open on the server
    if (hiddenUnresolvedCount > 0) {
      return (
        <div className="mt-4 -mx-6 -mb-6 px-6 py-3 border-t border-gray-200 text-center text-sm text-amber-700">
          {hiddenOpenText(hiddenUnresolvedCount)}
        </div>
      );
    }
    return (
      <div className="mt-4 -mx-6 -mb-6 px-6 py-3 border-t border-gray-200 flex items-center justify-center gap-2 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4" />
        All clarifications resolved
      </div>
    );
  }

  const cursor = cursorFor(questionSlug);
  const onTarget = Number.isInteger(cursor);
  const onlyThisOne = targets.length === 1 && onTarget;
  const position = onTarget
    ? `${cursor + 1} of ${targets.length} open`
    : `${targets.length} open`;

  const go = (direction: 'next' | 'prev') =>
    step(direction, { fromSlug: questionSlug, openDialog: true });

  return (
    <div className="mt-4 -mx-6 -mb-6 px-4 py-2 border-t border-gray-200 flex items-center justify-between">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => go('prev')}
        disabled={onlyThisOne}
      >
        <ChevronLeft className="h-4 w-4" />
        Previous
      </Button>
      <span className="text-xs text-gray-500">{position}</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => go('next')}
        disabled={onlyThisOne}
      >
        Next
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default ClarificationDialogStepper;
